/**
 * Три точки инструмента — те, что всегда под рукой.
 *
 * Точка держит не только цвет, но и толщину: на уроке переключаются не между
 * «цветами» и «размерами» по отдельности, а между готовыми перьями — тонкое
 * чёрное для решения, толстое красное для ошибок. Поэтому размер кружка на
 * панели показывает толщину: видно, чем будешь писать, ещё до нажатия.
 *
 * Выбор цвета открывается своим маленьким окошком у самой точки и ничего
 * не заменяет в панели: раньше он разворачивался внутри неё, и цвет
 * выбирался долго.
 */

const SLOT_COUNT = 3;

/** Вид линии. Повторяет PenStrokeDash, но без связи с модулем пера. */
export type SlotDash = "solid" | "dashed" | "dotted";

export type ColorSlot = {
  color: string;
  /** Толщина пера. У стикера не используется. */
  width: number;
  /**
   * Вид линии — свой у каждой точки.
   *
   * Раньше он был один на инструмент: выбрал пунктир у красного пера — и
   * чёрное тоже становилось пунктирным. А перья и заводят ради того, чтобы
   * переключаться между готовыми: сплошное чёрное для решения, пунктирное
   * красное для построений.
   */
  dash: SlotDash;
};

function normalizeHex(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  return `#${m[1].toLowerCase()}`;
}

function normalizeDash(v: unknown, fallback: SlotDash): SlotDash {
  return v === "solid" || v === "dashed" || v === "dotted" ? v : fallback;
}

function normalizeWidth(v: unknown, fallback: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(24, Math.max(1, Math.round(n)));
}

type Store = {
  key: string;
  event: string;
  defaults: readonly ColorSlot[];
  slots: ColorSlot[];
  /**
   * Какое перо сейчас в руке.
   *
   * По цвету это не определить: у двух точек цвет может совпасть, а у самого
   * инструмента цвет мог остаться от прежних настроек — тогда не подсвечивалась
   * ни одна точка, и было не видно, чем пишешь.
   */
  active: number;
};

function load(key: string, defaults: readonly ColorSlot[]): ColorSlot[] {
  const base = defaults.map((s) => ({ ...s }));
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return base;
    return base.map((fallback, i) => {
      const item = parsed[i];
      // Прежний вид записи — просто строка цвета: толщину берём свою.
      if (typeof item === "string") {
        return {
          color: normalizeHex(item) ?? fallback.color,
          width: fallback.width,
          dash: fallback.dash,
        };
      }
      if (item && typeof item === "object") {
        const rec = item as Record<string, unknown>;
        return {
          color: (typeof rec.color === "string" && normalizeHex(rec.color)) || fallback.color,
          width: normalizeWidth(rec.width, fallback.width),
          // У записей, сделанных до появления вида линии, его просто нет.
          dash: normalizeDash(rec.dash, fallback.dash),
        };
      }
      return fallback;
    });
  } catch {
    return base;
  }
}

function persist(store: Store): void {
  try {
    localStorage.setItem(store.key, JSON.stringify(store.slots));
  } catch {
    /* приватный режим — точки проживут до перезагрузки */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(store.event));
  }
}

function activeKey(key: string): string {
  return `${key}:active`;
}

/**
 * Какое перо было в руке в прошлый раз.
 *
 * Хранится рядом с самими точками, в localStorage: иначе доска, открытая
 * назавтра, брала первую точку, а перо — свой запасной чёрный, и выделенным
 * оказывался один цвет, а писало другим.
 */
function loadActive(key: string): number {
  try {
    const raw = localStorage.getItem(activeKey(key));
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n < SLOT_COUNT ? Math.trunc(n) : 0;
  } catch {
    return 0;
  }
}

function createStore(key: string, event: string, defaults: readonly ColorSlot[]): Store {
  return { key, event, defaults, slots: load(key, defaults), active: loadActive(key) };
}

function api(store: Store) {
  return {
    get: (): ColorSlot[] => store.slots,
    getActive: (): number => store.active,
    /** Запомнить, какое перо взяли. */
    setActive: (index: number): void => {
      if (index < 0 || index >= SLOT_COUNT || store.active === index) return;
      store.active = index;
      try {
        localStorage.setItem(activeKey(store.key), String(index));
      } catch {
        /* приватный режим — доживёт до перезагрузки */
      }
      if (typeof window !== "undefined") window.dispatchEvent(new Event(store.event));
    },
    subscribe: (onStoreChange: () => void) => {
      const handler = () => onStoreChange();
      window.addEventListener(store.event, handler);
      return () => window.removeEventListener(store.event, handler);
    },
    /** Изменить точку. Чужие точки не трогаем. */
    set: (index: number, patch: Partial<ColorSlot>): ColorSlot[] => {
      if (index < 0 || index >= SLOT_COUNT) return store.slots;
      const prev = store.slots[index]!;
      const next: ColorSlot = {
        color: patch.color ? normalizeHex(patch.color) ?? prev.color : prev.color,
        width: patch.width === undefined ? prev.width : normalizeWidth(patch.width, prev.width),
        dash: patch.dash === undefined ? prev.dash : normalizeDash(patch.dash, prev.dash),
      };
      if (
        next.color === prev.color &&
        next.width === prev.width &&
        next.dash === prev.dash
      ) {
        return store.slots;
      }
      store.slots = store.slots.map((s, i) => (i === index ? next : s));
      persist(store);
      return store.slots;
    },
  };
}

/** Перья: тонкое чёрное, среднее синее, толстое красное. */
export const penColorSlots = api(
  createStore("wb-pen-color-slots:v2", "wb-pen-color-slots", [
    { color: "#1a1a2e", width: 3, dash: "solid" },
    { color: "#2563eb", width: 5, dash: "solid" },
    { color: "#e11d48", width: 8, dash: "solid" },
  ])
);

/** Стикеры: жёлтый, зелёный, голубой. Толщина им не нужна. */
export const stickyColorSlots = api(
  createStore("wb-sticky-color-slots:v2", "wb-sticky-color-slots", [
    { color: "#fef08a", width: 1, dash: "solid" },
    { color: "#bbf7d0", width: 1, dash: "solid" },
    { color: "#bfdbfe", width: 1, dash: "solid" },
  ])
);

/**
 * Перо, которое было в руке, — для первой загрузки доски.
 *
 * Настройки пера живут в sessionStorage и в новой вкладке пусты; тогда их
 * собирают отсюда, чтобы доска открылась тем же пером, каким её закрыли.
 */
export function activePenSlot(): ColorSlot {
  const slots = penColorSlots.get();
  return slots[penColorSlots.getActive()] ?? slots[0]!;
}

export { SLOT_COUNT };
